import { Loader } from '@wix/design-system';
import React, { createContext, PropsWithChildren, useContext } from 'react';
import { useNotification } from '@wix/gg-components';
import { OrchestratorProvider } from '@wix/gg-components/orchestrator';
import { useCurrentStepId, usePendingRedirect, useClearPendingRedirect, useAppStore } from 'stores/appStore';
import { useFlowBuilder, useInitialFlowIndex, useStepSync, useRedirectCleanup, useIsLoggedIn } from 'hooks';
import styles from './FlowProvider.scss';

interface FlowContextValue {
  stepCount: number;
}

const FlowContext = createContext<FlowContextValue>({ stepCount: 0 });

export function useFlowContext() {
  return useContext(FlowContext);
}

// Syncs orchestrator's current step to Zustand store - enables useInitialFlowIndex to restore the correct step on refresh
function StepSync() {
  useStepSync();
  return null;
}

export function FlowProvider({ children }: PropsWithChildren) {
  const flow = useFlowBuilder();
  const { promptNotification } = useNotification();
  const currentStepId = useCurrentStepId();
  const pendingRedirect = usePendingRedirect();
  const clearPendingRedirect = useClearPendingRedirect();
  const setCurrentStepId = useAppStore((state) => state.setCurrentStepId);
  const isLoggedIn = useIsLoggedIn();

  const stepCount = flow.filter((item) => item.type === 'step').length;
  const initialIndex = useInitialFlowIndex(flow, pendingRedirect, currentStepId, isLoggedIn, setCurrentStepId);

  useRedirectCleanup(pendingRedirect, isLoggedIn, clearPendingRedirect);

  return (
    <FlowContext.Provider value={{ stepCount }}>
      <OrchestratorProvider
        flow={flow}
        initialIndex={initialIndex}
        onError={(error) => {
          promptNotification({ message: error, status: 'error' });
        }}
        loadingComponent={LoadingComponent}
      >
        <StepSync />
        {children}
      </OrchestratorProvider>
    </FlowContext.Provider>
  );
}

function LoadingComponent() {
  return (
    <div className={styles.loadingComponent}>
      <Loader />
    </div>
  );
}
